import createGraphqlClient from '@seek/ca-graphql-client';
import {
  browserHttpLink,
  nodeHttpLink,
} from '@seek/ca-graphql-client/src/http';
import {
  appName,
  environment,
  graphqlEndpointOverride,
  isProduction,
} from 'src/config';
import mockOverrides from './mock-overrides';
import resolverOverrides from './resolver-overrides';
import {
  type ApolloLink,
  InMemoryCache,
  isApolloError,
  type NormalizedCacheObject,
  type ServerError,
} from '@apollo/client';
import type { RequestHeaders } from 'src/server/server-render';
import type { ClientOptions } from '@seek/ca-graphql-client/src/types';
import type { Brand } from '@seek/melways-sites';
import type { Country } from 'src/types/globals';

const RATE_LIMITED = 'RATE_LIMITED';

const useAuth = environment !== 'dev' || environment !== 'development';
const mock = environment === 'development';

export const getGraphqlEndpointOverride = ({
  ssrMode,
  cookies,
  queryParams,
  isProduction: _isProduction,
  environment: _environment,
  graphqlEndpointOverride: _graphqlEndpointOverride,
}: {
  ssrMode: boolean;
  cookies?: Record<string, string>;
  queryParams?: URLSearchParams;
  isProduction: boolean;
  environment: string;
  graphqlEndpointOverride: string;
}) => {
  // use query param darkProd if it exists
  const darkProd = queryParams?.get('darkProd');
  if (typeof darkProd === 'string') {
    const branchParam =
      !darkProd || darkProd === 'master' ? '' : `?branch=${darkProd}`;

    return `https://candidate-graphql-dark-prod.cloud.seek.com.au/graphql${branchParam}`;
  }

  // if STAGING_GRAPHQL_OVERRIDE cookie exists, use dark prod gql endpoint on staging
  if (_environment === 'stag' && Boolean(cookies?.STAGING_GRAPHQL_OVERRIDE)) {
    return 'https://candidate-graphql-dark-prod.cloud.seek.com.au/';
  }

  // on server-side prod, use the prod graphql endpoint
  if (ssrMode && _isProduction) {
    return 'http://candidate-graphql.int-cloud.seek.com.au';
  }

  // otherwise use what's in the config
  return _graphqlEndpointOverride;
};

export const getHeaders = ({
  ssrMode,
  reqHeaders,
  cookies,
  environment: _environment,
  queryParams,
}: {
  ssrMode: boolean;
  reqHeaders?: RequestHeaders;
  cookies?: Record<string, string>;
  environment: string;
  queryParams?: URLSearchParams;
}) => {
  const sourceExperienceReference =
    queryParams?.get('sourceExperience') || queryParams?.get('ref');

  let headers: Record<string, string | string[] | null | undefined> = {
    ...(sourceExperienceReference && {
      'seek-traffic-source': sourceExperienceReference,
    }),
  };

  if (_environment === 'dev') {
    headers = {
      ...headers,
      Authorization: cookies?.AUTH_TOKEN,
    };
  }

  if (ssrMode) {
    headers = {
      ...headers,
      'x-real-ip': reqHeaders!['x-real-ip'],
      'user-agent': 'chalice',
    };
  }
  return headers;
};

export const getClient = ({
  brand,
  country,
  links = [],
  reqHeaders,
  cacheData = {},
  cookies,
  queryParams,
}: {
  brand: Brand;
  country: Country;
  links?: ApolloLink[];
  reqHeaders?: RequestHeaders;
  cacheData?: NormalizedCacheObject;
  cookies?: Record<string, string>;
  queryParams?: URLSearchParams;
}) => {
  const httpLink = ENV.SERVER ? nodeHttpLink : browserHttpLink;
  const ssrMode = Boolean(ENV.SERVER);

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          jobDetails: {
            keyArgs: ['id', 'locale', 'zone'],
            merge: true,
          },
          viewer: {
            merge: true,
          },
        },
      },
    },
  });

  if (ENV.CLIENT) {
    cache.restore(cacheData);
  }

  return createGraphqlClient({
    headers: getHeaders({
      ssrMode,
      reqHeaders,
      cookies,
      environment,
      queryParams,
    }),
    appName,
    brand: brand as ClientOptions['brand'],
    country: country as ClientOptions['country'],
    graphqlEndpointOverride: getGraphqlEndpointOverride({
      ssrMode,
      cookies,
      queryParams,
      environment,
      isProduction,
      graphqlEndpointOverride,
    }),
    authOptions: {
      useAuth,
      forceStrategy: 'AUTH0',
    },
    mock,
    mockOverrides: mockOverrides(),
    resolverOverrides,
    links,
    httpLink,
    cache,
  });
};

export const isWAFForbiddenError = (error: any) =>
  isApolloError(error) &&
  (error?.networkError as ServerError)?.statusCode === 403;

export const isGraphQLRateLimitedError = (error: any) =>
  (isApolloError(error) &&
    error?.graphQLErrors?.length > 0 &&
    error?.graphQLErrors[0]?.extensions?.code === RATE_LIMITED) ||
  error?.customData?.errors[0]?.extensions?.code === RATE_LIMITED;
